import { useState } from "react";

interface Activity {
    description: string;
}

interface ExperienceItemProps {
    role: string;
    company: string;
    activities: Activity[];
}

function ExperienceItem({ role, company, activities }: ExperienceItemProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="border-l border-gray-400 p-6 flex flex-col justify-between group hover:bg-black/5 transition-colors duration-300">
            <div className="space-y-4">
                <div
                    className="cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <h3 className="font-display uppercase text-4xl md:text-5xl font-black leading-[0.85] break-words">
                        {role}
                    </h3>
                    <p className="text-gray-600 text-sm mt-2 font-medium tracking-tight uppercase">
                        {company}
                    </p>
                </div>

                {isExpanded && (
                    <div className="mt-6 animate-in fade-in slide-in-from-top-2 duration-300">
                        <ul className="space-y-3">
                            {activities.map((activity, index) => (
                                <li key={index} className="text-sm text-gray-700 flex gap-3 max-w-md">
                                    <span className="text-black font-bold">—</span>
                                    <span>{activity.description}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-8 text-[10px] uppercase font-bold tracking-widest text-gray-400 hover:text-black transition-colors self-start"
            >
                {isExpanded ? "[ Hide Details ]" : "[ Show Details ]"}
            </button>
        </div>
    );
}

export function Experience() {
    const experiences = [
        {
            role: "Software Development Engineer",
            company: "Healthatom",
            activities: [
                { description: "Developed a React-based insurance reconciliation module with advanced filtering, case management, and document handling, enabling clinics to track insurer payments and manage reconciliation and appeal workflows." },
                { description: "Built the frontend for an AI-powered Clinical Summary feature using React and MobX, delivering a fast, user-friendly modal that helps healthcare professionals quickly understand patient context, treatment status, and clinical history." },
                { description: "Actively developed core features for the Whitelabel Insurance product, implementing eligibility checks, multiple user touchpoints, and a React-based insurance offer flow integrated into daily clinical workflows." },
                { description: "Implemented asynchronous electronic billing enhancements, improving invoice and credit note retrieval, provider-specific integrations, and user feedback for delayed document generation." }
            ]
        },
        {
            role: "Technical leader Jr",
            company: "Un toque digital",
            activities: [
                { description: "Lead and guide the development team to achieve project objectives, proactively identifying and resolving impediments." },
                { description: "Manage project stories and tasks using agile methodologies, ensuring timely and high-quality delivery." },
                { description: "Foster team motivation and promote knowledge sharing and collaboration within the development team." },
                { description: "Contribute to the design and definition of software architecture for multiple projects." },
            ]
        },
        {
            role: "Python Developer",
            company: "TicSocial",
            activities: [
                { "description": "Design and develop a hospital module using Django for the scheduling and management of chemotherapy treatments for patients." },
                { "description": "Develop a module for the management and control of medications for cancer patients undergoing scheduled chemotherapy treatments." },
                { "description": "Gather and analyze customer software requirements to design and implement new customized features." },
                { "description": "Perform maintenance, optimization, and enhancements on existing hospital software functionalities." }
            ]
        },
        {
            role: "Fullstack Developer",
            company: "Un toque digital",
            activities: [
                { "description": "Design and develop an educational web platform for simulating SABER ICFES-style exams, including interactive content visualization, using CodeIgniter." },
                { "description": "Develop a RESTful API using Sanic to support a mobile application for human skills training courses." },
                { "description": "Build a web platform with CodeIgniter for selling courses focused on the development of human and soft skills." },
                { "description": "Implement a PayU payment gateway using PHP for an educational e-commerce website offering courses in the creative and financial industries." }
            ]
        }
    ];

    return (
        <section className="py-24 w-full" id="experience">
            <div className="max-w-7xl mx-auto">
                <div className="mb-16">
                    <h2 className="font-display uppercase text-7xl md:text-9xl font-black leading-[0.85] text-neutral">
                        History
                    </h2>
                    <div className="w-12 h-[2px] bg-black mt-6"></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px">
                    {experiences.map((exp, index) => (
                        <div key={index} className="bg-primary">
                            <ExperienceItem
                                role={exp.role}
                                company={exp.company}
                                activities={exp.activities}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default Experience;
